import type { DeepPartial } from "@apollo/client/utilities";

import { EmptyState } from "@/components/shared/empty-state";
import type { Account } from "@/graphql/codegen/graphql";
import { formatUSDCents } from "@/utils/currency";
import type { DiscoverWidgetPreset } from "@/utils/discover-widget";

import type { TimePeriod } from "../../discover-sort-constants";
import { DiscoverWidgetUserRowSkeleton } from "./discover-widget-user-row-skeleton";
import { SidebarTopResultsWidgetRow } from "./sidebar-top-results-widget-row";
import { WidgetPreset } from "./sidebar-top-results-widget-time-period";
import { useDiscoverWidgetQuery } from "./use-discover-widget-query";

type TopJoinersWidgetViewProps = {
  selectedTimePeriod: TimePeriod;
  baseRedirectUrl?: string;
  preset?: DiscoverWidgetPreset;
};

export const TopJoinersWidgetView = ({
  selectedTimePeriod,
  baseRedirectUrl,
  preset = WidgetPreset.topJoiners,
}: TopJoinersWidgetViewProps) => {
  const { initialLoading, items, loadingMarkup, loadingMore } =
    useDiscoverWidgetQuery({
      preset,
      timePeriod: selectedTimePeriod,
    });
  const joiners = items as DeepPartial<Account>[];

  return (
    <div className="flex w-full flex-col items-start justify-between">
      {!initialLoading && joiners.length === 0 && !loadingMore && (
        <div className="flex size-full items-center justify-center p-2 py-4">
          <EmptyState
            image="/assets/empty-31.png"
            title="No results."
            description="Try adjusting the filters."
          />
        </div>
      )}
      <div
        className="
          custom-scrollbar custom-scrollbar-hover-only h-[400px] w-full
        "
      >
        {joiners.map((account: DeepPartial<Account>) => {
          const publicKey = account?.publicKey;
          const username = account?.username;
          const extraData = account?.extraData;

          return (
            <SidebarTopResultsWidgetRow
              key={publicKey}
              username={username}
              publicKey={publicKey}
              extraData={extraData}
              baseRedirectUrl={baseRedirectUrl}
            >
              <>
                <p
                  className="
                    font-shadow-green font-mono text-xs font-medium
                    text-emerald-400
                  "
                >
                  {formatUSDCents(account?.totalBalanceUsdCents ?? 0)}
                </p>
                <p className="text-xs text-muted">Assets</p>
              </>
            </SidebarTopResultsWidgetRow>
          );
        })}
        {initialLoading ? (
          <DiscoverWidgetUserRowSkeleton count={10} />
        ) : (
          loadingMarkup
        )}
      </div>
    </div>
  );
};
